import React, { useState, useEffect } from "react";
import _ from "lodash";
import {
  TextInput,
  Select,
  Textarea,
  NumberInput,
  Group,
  Title,
  Text,
  Divider,
  Button,
  Autocomplete,
} from "@mantine/core";
import ReactJson from "react-json-view";

import ArrayTable from "./arrayTable";
import { DATA_CURRENCY, DATA_GI_CHARGE_ITEMS } from "../data/options";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { useFormatter } from "../hooks/useFomatter";
import { useForceUpdate } from "@mantine/hooks";
import { IconAlertTriangle } from "@tabler/icons";

import RichTextEditor from "./richTextEditor";
import FormReadOnlyField from "./formReadOnlyField";
import { isWidgetReadOnly } from "../helpers/formReadOnly";
import { formatFormFieldDisplay } from "../helpers/formatFormFieldDisplay";

export const calculateTotal = (charges) => {
  if (_.isEmpty(charges)) return 0;
  const total = charges?.reduce((a, c) => {
    const lineAmount = (c.qty ?? 1) * c.unitPrice;
    return a + +lineAmount.toFixed(2);
  }, 0);
  return +total.toFixed(2);
};

/** API / inputs may store total as string; compare numerically to avoid setFieldValue every render. */
const chargeTotalsMatch = (cur, computedTotal) => {
  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : NaN;
  };
  const a = toNum(cur);
  const b = toNum(computedTotal);
  if (Number.isFinite(a) && Number.isFinite(b)) {
    return Math.abs(a - b) < 0.005;
  }
  return cur === computedTotal;
};

export const IA_LEVY_RATE = 0.001;
export const IA_LEVY_CAP = 5000;
export const EC_LEVY_RATE = 0.108;
export const MIB_LEVY_RATE = 0.03;

const DebitNoteChargeItems = ({
  form,
  name,
  readonly = false,
  readOnly = false,
  disabled = false,
}) => {
  const chargeItems = _.get(form.values, "chargeItems");
  const costTotal = _.get(form.values, "costTotal");
  const paymentStatus = _.get(form.values, "paymentStatus");
  const PAYMENT_STATUS_TO_LOCK =
    process.env.REACT_APP_LOCK_DN_PAYMENT_STATUS?.split(",") ?? [];
  const allowToEdit = !PAYMENT_STATUS_TO_LOCK.includes(paymentStatus);
  const fieldsLocked =
    isWidgetReadOnly({ disabled, readOnly, readonly, form }) || !allowToEdit;

  const [total, setTotal] = useState(0);
  const [formatter] = useFormatter();
  const [discountAmount, setDiscountAmount] = useState(
    _.get(form.values, "discountAmount"),
  );
  const [clientFixRate, setClientFixRate] = useState(
    _.get(form.values, "clientFixRate"),
  );
  const [insuredValue, setInsuredValue] = useState(
    _.get(form.values, "insuredValue"),
  );

  const forceUpdate = useForceUpdate();

  const defaultClientFixRate = process.env
    .REACT_APP_DEBIT_NOTE_CHARGE_DEFAULT_CLIENT_FIX_RATE
    ? +process.env.REACT_APP_DEBIT_NOTE_CHARGE_DEFAULT_CLIENT_FIX_RATE
    : 0;

  const rowInitValue = {
    item: "",
    currency: "HKD",
    qty: 1,
    unitPrice: 0,
    price: 0,
  };

  const updateIALevy = (iaLevyItems, premiumItem) => {
    if (!iaLevyItems || !premiumItem) return;
    iaLevyItems.forEach((iaLevyItem) => {
      iaLevyItem.qty = 1;
      iaLevyItem.unitPrice = +Math.min(
        premiumItem.unitPrice * IA_LEVY_RATE,
        IA_LEVY_CAP,
      ).toFixed(2);
      iaLevyItem.price = +(iaLevyItem.unitPrice * iaLevyItem.qty).toFixed(2);
    });
  };

  const updateECLevy = (ecLevyItems, premiumItem) => {
    if (!ecLevyItems || !premiumItem) return;
    ecLevyItems.forEach((ecLevyItem) => {
      ecLevyItem.qty = 1;
      ecLevyItem.unitPrice = +(premiumItem.unitPrice * EC_LEVY_RATE).toFixed(2);
      ecLevyItem.price = +(ecLevyItem.unitPrice * ecLevyItem.qty).toFixed(2);
    });
  };

  const updateMIBLevy = (mibLevyItems, premiumItem) => {
    if (!mibLevyItems || !premiumItem) return;
    mibLevyItems.forEach((mibLevyItem) => {
      mibLevyItem.qty = 1;
      mibLevyItem.unitPrice = +(premiumItem.unitPrice * MIB_LEVY_RATE).toFixed(
        2,
      );
      mibLevyItem.price = +(mibLevyItem.unitPrice * mibLevyItem.qty).toFixed(2);
    });
  };

  // const onItemSubmit = ({ value }) => {
  //   const premiumItem = chargeItems.find((c) => c.item === "Premium");
  //   if (!premiumItem) return;

  //   const iaLevyItems = chargeItems.filter((c) => c.item === "IA Levy");
  //   const ecLevyItems = chargeItems.filter((c) => c.item === "EC Levy");
  //   const mibLevyItems = chargeItems.filter((c) => c.item === "MIB Levy");

  //   switch (value) {
  //     case "Premium":
  //       updateIALevy(iaLevyItems, premiumItem);
  //       updateECLevy(ecLevyItems, premiumItem);
  //       updateMIBLevy(mibLevyItems, premiumItem);
  //       break;
  //     case "IA Levy":
  //       updateIALevy(iaLevyItems, premiumItem);
  //       break;

  //     case "EC Levy":
  //       updateECLevy(ecLevyItems, premiumItem);
  //       break;
  //     case "MIB Levy":
  //       updateMIBLevy(mibLevyItems, premiumItem);
  //       break;
  //   }
  // };

  const fields = [
    {
      header: "Item",
      headerWidth: "50%",
      fieldName: "item",
      component: Autocomplete,
      props: {
        data: DATA_GI_CHARGE_ITEMS,
        searchable: true,
        clearable: true,
      },
    },

    {
      header: "Qty",
      headerWidth: "10%",
      fieldName: "qty",
      component: NumberInput,
      props: {},
    },
    {
      header: "Currency",
      headerWidth: "12%",
      fieldName: "currency",
      component: Select,
      props: {
        data: DATA_CURRENCY,
        defaultValue: "HKD",
        searchable: true,
      },
    },
    {
      header: "Unit Price",
      headerWidth: "30%",
      fieldName: "unitPrice",
      component: NumberInput,
      props: {
        parser: formatter.currencyParser,
        formatter: formatter.currencyFormat,
        //  prefix="$"
        // decimalSeparator=","
        precision: 2,
      },
    },
  ];

  const onChargeChange = (charges, index, fieldName) => {
    form.markUserEdited?.();
    // console.log("onChargeChange", charges, index, fieldName);
    const iaLevyItems = charges.filter((c) => c.item === "IA Levy");
    const ecLevyItems = charges.filter((c) => c.item === "EC Levy");
    const mibLevyItems = charges.filter((c) => c.item === "MIB Levy");
    const premiumItem = charges.find((c) => /premium/i.test(c.item));

    if (fieldName === "unitPrice") {
      if (premiumItem && /premium/i.test(charges[index].item)) {
        updateIALevy(iaLevyItems, premiumItem);
        updateECLevy(ecLevyItems, premiumItem);
        updateMIBLevy(mibLevyItems, premiumItem);
        form.setFieldValue("chargeItems", charges);
      }
    }

    if (fieldName === "item") {
      if (premiumItem && /IA/i.test(charges[index].item)) {
        updateIALevy(iaLevyItems, premiumItem);
      }
      if (premiumItem && /EC/i.test(charges[index].item)) {
        updateECLevy(ecLevyItems, premiumItem);
      }
      if (premiumItem && /MIB/i.test(charges[index].item)) {
        updateMIBLevy(mibLevyItems, premiumItem);
      }
    }

    // Always calculate and update total regardless of the field that changed
    const total = calculateTotal(charges);
    setTotal(total);
    // console.log("onChargeChange total", total);

    // Also update the form immediately to ensure other components get notified
    form.setFieldValue("chargeItems", charges);
  };

  const onRemoveRow = (charges, index) => {
    const total = calculateTotal(charges);
    setTotal(total);
  };

  // Initialize total on component mount
  useEffect(() => {
    const initialTotal = calculateTotal(chargeItems);
    setTotal(initialTotal);
    // console.log("DebitNoteChargeItems: initial total", initialTotal);
  }, []);

  // Update form when total changes
  useEffect(() => {
    const cur = _.get(form.values, "total");
    if (chargeTotalsMatch(cur, total)) return;
    form.setFieldValue("total", total);
    // console.log("DebitNoteChargeItems: setting form total", total);
  }, [total]);

  // Also watch for chargeItems changes to recalculate total
  useEffect(() => {
    const newTotal = calculateTotal(chargeItems);
    if (newTotal !== total) {
      setTotal(newTotal);
      // console.log(
      //   "DebitNoteChargeItems: chargeItems changed, new total",
      //   newTotal
      // );
    }
  }, [chargeItems]);

  const applyDiscount = () => {
    // console.log("discount", discountAmount);
    form.setFieldValue("discountAmount", discountAmount);
    forceUpdate();
  };

  const applyFixRate = () => {
    // console.log("applyFixRate", clientFixRate, insuredValue);
    if (!clientFixRate) return;
    if (!insuredValue) return;

    let newChargeItems = [];
    if (!_.isEmpty(chargeItems)) {
      newChargeItems = _.cloneDeep(chargeItems);
    } else {
      newChargeItems = [
        {
          item: "Premium",
          unitPrice: 0,
          price: 0,
          qty: 1,
          currency: "HKD",
        },
        {
          item: "IA Levy",
          unitPrice: 0,
          price: 0,
          qty: 1,
          currency: "HKD",
        },
      ];
    }
    // console.log("newChargeItems", newChargeItems);

    const pItem = newChargeItems.find((c) => c.item === "Premium");
    if (pItem) {
      let unitPrice = (insuredValue * clientFixRate) / 100;
      pItem.price = unitPrice * pItem.qty;
      pItem.unitPrice = unitPrice;
      pItem.item = "Premium";
    }
    form.setFieldValue("chargeItems", newChargeItems);
    form.setFieldValue("clientFixRate", clientFixRate);
    form.setFieldValue("insuredValue", insuredValue);

    const IA_LEVY_ITEM = newChargeItems.filter((c) => c.item === "IA Levy");
    const EC_LEVY_ITEM = newChargeItems.filter((c) => c.item === "EC Levy");
    const MIB_LEVY_ITEM = newChargeItems.filter((c) => c.item === "MIB Levy");
    updateIALevy(IA_LEVY_ITEM, pItem);
    updateECLevy(EC_LEVY_ITEM, pItem);
    updateMIBLevy(MIB_LEVY_ITEM, pItem);

    setTotal(
      calculateTotal(newChargeItems) +
        (IA_LEVY_ITEM?.price ?? 0) +
        (EC_LEVY_ITEM?.price ?? 0) +
        (MIB_LEVY_ITEM?.price ?? 0),
    );
    forceUpdate();
  };

  const showFixRate = process.env.REACT_APP_DEBIT_NOTE_SHOW_FIX_RATE === "true";

  return (
    <>
      {/* readonly: {readonly ? "true" : "false"} <br /> */}
      {/* allowToEdit: {allowToEdit ? "true" : "false"} <br /> */}
      {/* fixRate:{_.get(form.values, "clientFixRate")} */}
      {/* <ReactJson src={PAYMENT_STATUS_TO_LOCK} style={{ background: "white" }} />
      paymentStatus: {paymentStatus} <br />
      readonly: {readonly ? "true" : "false"} <br />
      allowToEdit: {allowToEdit ? "true" : "false"} <br /> */}
      {total > 0 && form.values.costTotal === 0 && (
        <Group mb="xl">
          <IconAlertTriangle size="14" color="red" />
          <Text color={"red"} size="md">
            Cost Items is 0
          </Text>
        </Group>
      )}
      <Group position="apart">
        <Group align="end" mb="xl">
          {fieldsLocked ? (
            <FormReadOnlyField
              label="Exclusive Discount (HKD)"
              value={formatFormFieldDisplay(_.get(form.values, "discountAmount"), {
                kind: "number",
                props: {
                  precision: 2,
                  formatter: formatter.currencyFormat,
                },
              })}
            />
          ) : (
            <>
              <NumberInput
                size="xs"
                precision={2}
                label="Exclusive Discount (HKD)"
                placeholder="10"
                value={_.get(form.values, "discountAmount")}
                onChange={(value) => {
                  form.markUserEdited?.();
                  setDiscountAmount(value);
                  form.setFieldValue("discountAmount", value);
                }}
                parser={formatter.currencyParser}
                formatter={formatter.currencyFormat}
              ></NumberInput>
              <Button size="xs" color="blue" onClick={() => applyDiscount()}>
                Apply Discount
              </Button>
            </>
          )}
        </Group>

        {showFixRate &&
          (fieldsLocked ? (
            <Group align="end" mb="xl">
              <FormReadOnlyField
                label="Insured Value"
                value={formatFormFieldDisplay(insuredValue, {
                  kind: "number",
                  props: {
                    precision: 2,
                    formatter: formatter.currencyFormat,
                  },
                })}
              />
              <FormReadOnlyField
                label="Client Fix Rate (%)"
                value={formatFormFieldDisplay(
                  _.get(form.values, "clientFixRate") ?? defaultClientFixRate,
                  { kind: "number", props: { precision: 5 } }
                )}
              />
            </Group>
          ) : (
            <Group align="end" mb="xl">
              <NumberInput
                size="xs"
                precision={2}
                label="Insured Value"
                placeholder="1000000"
                value={insuredValue}
                onChange={(value) => {
                  form.markUserEdited?.();
                  setInsuredValue(value);
                  form.setFieldValue("insuredValue", value);
                }}
                parser={formatter.numberParser}
                formatter={formatter.currencyFormat}
              ></NumberInput>
              <NumberInput
                size="xs"
                precision={5}
                label="Client Fix Rate (%)"
                placeholder="3.2"
                value={
                  _.get(form.values, "clientFixRate") ?? defaultClientFixRate
                }
                onChange={setClientFixRate}
                parser={formatter.numberParser}
                formatter={(value) =>
                  !Number.isNaN(parseFloat(value)) ? `${value}%` : "%"
                }
              ></NumberInput>

              <Button size="xs" color="teal" onClick={() => applyFixRate()}>
                Apply Rate
              </Button>
            </Group>
          ))}
      </Group>
      <ArrayTable
        form={form}
        name={name}
        rowInitValue={rowInitValue}
        fields={fields}
        onDataChange={onChargeChange}
        onRemoveRow={onRemoveRow}
        readonly={fieldsLocked}
        // readonly={true}
      />
    </>
  );
};

export default DebitNoteChargeItems;
